import { useMemo, useRef } from 'react'
import i18n from 'i18next'
import { initReactI18next, useTranslation } from 'react-i18next'
import { safeLocalStorage } from '@/lib/storage'
import enTranslation from './locales/en/translation'
import deTranslation from './locales/de/translation'
import {entityNounMarkupPostProcessor, POST_PROCESSOR_NAME} from './i18n/entity-noun-markup'
import type { TFunction } from 'i18next'

const LANGUAGE_STORAGE_KEY = 'iam-language'

function getSavedLanguage(): string | null {
  return safeLocalStorage.get(LANGUAGE_STORAGE_KEY)
}

i18n.use(initReactI18next).use(entityNounMarkupPostProcessor).init({
  resources: {
    en: { translation: enTranslation },
    de: { translation: deTranslation },
  },
  lng: getSavedLanguage() ?? 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  postProcess: [POST_PROCESSOR_NAME],
})

syncDocumentLang(i18n.language)
i18n.on('languageChanged', syncDocumentLang)

function syncDocumentLang(lng: string): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng
  }
}

export function changeLanguage(lng: string): void {
  safeLocalStorage.set(LANGUAGE_STORAGE_KEY, lng)
  i18n.changeLanguage(lng)
  onLanguageChange?.(lng)
}

let onLanguageChange: ((lng: string) => void) | null = null

export function setOnLanguageChange(callback: (lng: string) => void): void {
  onLanguageChange = callback
}

export function getCurrentLanguage(): string {
  return i18n.language
}

type ExtractParams<S extends string> =
  S extends `${string}{{${infer P}}}${infer Rest}` ? P | ExtractParams<Rest> : never

type PluralSuffix = '_zero' | '_one' | '_two' | '_few' | '_many' | '_other'

type HasPluralSibling<T, K extends string | number | symbol> =
  K extends string
    ? Extract<keyof T, `${K}${PluralSuffix}`> extends never
      ? false
      : true
    : false

type I18nProxy<T> = {
  [K in keyof T]: T[K] extends string
    ? ExtractParams<T[K]> extends never
      ? HasPluralSibling<T, K> extends true
        ? (params: { count: number }) => string
        : string
      : (params: Record<ExtractParams<T[K]>, string | number>) => string
    : T[K] extends Record<string, unknown>
      ? I18nProxy<T[K]>
      : never
}

const PLURAL_SUFFIXES = ['_zero', '_one', '_two', '_few', '_many', '_other'] as const

function resolve(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (o as Record<string, unknown>)?.[k], obj)
}

function hasPluralSibling(parent: Record<string, unknown>, prop: string): boolean {
  for (const suffix of PLURAL_SUFFIXES) {
    if (`${prop}${suffix}` in parent) return true
  }
  return false
}

function createProxy(tRef: { current: TFunction }, prefix: string): unknown {
  return new Proxy(Object.create(null), {
    get(_, prop: string) {
      const key = prefix ? `${prefix}.${prop}` : prop
      const value = resolve(enTranslation, key)
      if (typeof value === 'object' && value !== null) return createProxy(tRef, key)
      if (typeof value === 'string' && value.includes('{{')) {
        return (params: Record<string, string | number>) => tRef.current(key, params)
      }
      const parent = (prefix
        ? resolve(enTranslation, prefix)
        : enTranslation) as Record<string, unknown> | undefined
      if (parent && hasPluralSibling(parent, prop)) {
        return (params: Record<string, string | number>) => tRef.current(key, params)
      }
      return tRef.current(key)
    },
  })
}

export function useI18n(): I18nProxy<typeof enTranslation> {
  const { t } = useTranslation()
  const tRef = useRef(t)
  tRef.current = t
  // Stable proxy: created once, always reads latest t via ref.
  // Safe as useCallback/useMemo dependency — identity never changes.

  return useMemo(() => createProxy(tRef, '') as I18nProxy<typeof enTranslation>, [])
}

export default i18n
